import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ModuleNav, { MODULE_NAVS } from '../../components/ModuleNav';
import {
  Alert, Button, Card, DatePicker, Form, Input, InputNumber, Modal, Popconfirm, Radio, Result, Select, Space, Table, Tag, Typography, notification,
} from 'antd';
import { DeleteOutlined, EditOutlined, NotificationOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import lib from '../../assets/utils/lib';
import { usePermissions } from '../../assets/utils/usePermissions';
import { states } from '../../assets/utils/config';

const { Title, Paragraph, Text } = Typography;

const ANNOUNCEMENTS_PERMS = ['announcements.manage', 'admin.*'];

/** The banner as the member sees it: one line, marquee if long, CTA pinned right. */
function BannerPreview({ message, ctaLabel }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#0f2f4f', color: '#fff', borderRadius: 10, padding: '10px 12px', overflow: 'hidden' }}>
      <div style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 13 }}>
        {message || <span style={{ opacity: 0.6 }}>Your message will appear here</span>}
      </div>
      {ctaLabel ? (
        <span style={{ flex: '0 0 auto', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', background: '#fff', color: '#0f2f4f', borderRadius: 999, padding: '4px 12px', fontSize: 12, fontWeight: 600 }}>
          {ctaLabel}
        </span>
      ) : null}
      <span style={{ flex: '0 0 auto', opacity: 0.6 }}>×</span>
    </div>
  );
}

export default function AnnouncementsPage() {
  const { hasAnyPermission } = usePermissions();
  const allowed = hasAnyPermission(ANNOUNCEMENTS_PERMS);

  const [rows, setRows] = useState([]);
  const [options, setOptions] = useState({ routes: [], landing_pages: [], plans: [], categories: [], organizations: [], access_types: [] });
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | {} (new) | row
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const ctaKind = Form.useWatch('cta_kind', form);
  const message = Form.useWatch('message', form);
  const ctaLabel = Form.useWatch('cta_label', form);

  const load = useCallback(async () => {
    setLoading(true);
    const [list, opts] = await Promise.all([lib.announcements.list(), lib.announcements.options()]);
    setRows(list?.data?.announcements || []);
    if (opts?.data) setOptions(opts.data);
    setLoading(false);
  }, []);

  useEffect(() => { if (allowed) load(); }, [allowed, load]);

  const openNew = () => {
    form.resetFields();
    form.setFieldsValue({ cta_kind: 'none', priority: 0 });
    setEditing({});
  };
  const openEdit = (row) => {
    const isRoute = row.cta_href && row.cta_href.startsWith('/(app)');
    form.setFieldsValue({
      message: row.message,
      cta_kind: row.cta_href ? (isRoute ? 'route' : 'url') : 'none',
      cta_label: row.cta_label || '',
      cta_route: isRoute ? row.cta_href : undefined,
      cta_url: !isRoute ? row.cta_href || '' : '',
      window: [row.starts_at ? dayjs(row.starts_at) : null, row.ends_at ? dayjs(row.ends_at) : null],
      priority: Number(row.priority || 0),
      target_plan_ids: row.target_plan_ids ? row.target_plan_ids.split(',').map(Number) : [],
      target_states: row.target_states ? row.target_states.split(',') : [],
      target_categories: row.target_categories ? row.target_categories.split(',') : [],
      target_policy_nos: row.target_policy_nos || '',
      target_org_ids: row.target_org_ids ? row.target_org_ids.split(',').map(Number) : [],
      target_access_types: row.target_access_types ? row.target_access_types.split(',') : [],
      public_id: row.public_id,
    });
    setEditing(row);
  };

  const save = async () => {
    let v;
    try { v = await form.validateFields(); } catch (e) { return; }
    const payload = {
      message: v.message,
      cta_label: v.cta_kind === 'none' ? '' : v.cta_label,
      cta_href: v.cta_kind === 'route' ? v.cta_route : (v.cta_kind === 'url' ? v.cta_url : ''),
      starts_at: v.window?.[0] ? v.window[0].format('YYYY-MM-DD HH:mm:ss') : null,
      ends_at: v.window?.[1] ? v.window[1].format('YYYY-MM-DD HH:mm:ss') : null,
      priority: v.priority || 0,
      target_plan_ids: (v.target_plan_ids || []).join(','),
      target_states: (v.target_states || []).join(','),
      target_categories: (v.target_categories || []).join(','),
      target_policy_nos: v.target_policy_nos || '',
      target_org_ids: (v.target_org_ids || []).join(','),
      target_access_types: (v.target_access_types || []).join(','),
      ...(editing?.id ? {} : { public_id: v.public_id || '' }),
    };
    setSaving(true);
    const res = editing?.id ? await lib.announcements.update(editing.id, payload) : await lib.announcements.create(payload);
    setSaving(false);
    if (res?.error) {
      notification.error({ message: res.message || 'Could not save', placement: 'topLeft', duration: 8 });
      return;
    }
    notification.success({ message: res?.message || 'Saved', placement: 'topLeft', duration: 6 });
    setEditing(null);
    load();
  };

  const archive = async (row) => {
    const res = await lib.announcements.archive(row.id);
    if (res?.error) { notification.error({ message: res.message, placement: 'topLeft' }); return; }
    notification.success({ message: res?.message || 'Archived', placement: 'topLeft' });
    load();
  };

  const orgName = useMemo(() => {
    const m = {}; (options.organizations || []).forEach((o) => { m[o.org_id] = o.org_name; }); return m;
  }, [options]);
  const accessLabel = useMemo(() => {
    const m = {}; (options.access_types || []).forEach((a) => { m[a.value] = a.label; }); return m;
  }, [options]);
  const landingLabel = useMemo(() => {
    const m = {}; (options.landing_pages || []).forEach((l) => { m[l.href] = l.label; }); return m;
  }, [options]);
  const planName = useMemo(() => {
    const m = {}; (options.plans || []).forEach((p) => { m[p.plan_id] = p.plan_name; }); return m;
  }, [options]);

  if (!allowed) {
    return <Result status="403" title="Not available" subTitle="Managing announcements requires the announcements.manage permission." />;
  }

  return (
    <div style={{ padding: 16 }}>
      <Space align="center" style={{ justifyContent: 'space-between', width: '100%', marginBottom: 8 }} wrap>
        <Title level={3} style={{ margin: 0 }}><NotificationOutlined /> Announcements</Title>
        <Space>
          <Button icon={<ReloadOutlined />} loading={loading} onClick={load}>Refresh</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openNew}>New announcement</Button>
        </Space>
      </Space>
      <ModuleNav items={MODULE_NAVS.campaigns} />
      <Paragraph type="secondary">
        The banner under the greeting on the member app&apos;s home screen. Members see <strong>one</strong> announcement at a time: the highest-priority one that is live for them. Dismissals are remembered on the phone against the announcement&apos;s id, so an edited message keeps its id and a new message gets a new one.
      </Paragraph>

      <Table
        size="small"
        rowKey="id"
        loading={loading}
        dataSource={rows}
        pagination={{ pageSize: 20 }}
        columns={[
          {
            title: 'Announcement',
            key: 'msg',
            render: (_, r) => (
              <div style={{ maxWidth: 520 }}>
                <BannerPreview message={r.message} ctaLabel={r.cta_label} />
                <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                  id <Text code>{r.public_id}</Text>
                  {r.cta_href ? <> · opens {landingLabel[r.cta_href] ? <Text strong>{landingLabel[r.cta_href]}</Text> : <Text code>{r.cta_href}</Text>}</> : null}
                </div>
              </div>
            ),
          },
          {
            title: 'Live',
            key: 'live',
            width: 200,
            render: (_, r) => (
              <div style={{ fontSize: 12 }}>
                {Number(r.status) !== 1 ? <Tag>archived</Tag> : (r.is_live ? <Tag color="green">live now</Tag> : <Tag color="gold">scheduled / ended</Tag>)}
                <div style={{ color: '#888' }}>
                  {r.starts_at ? dayjs(r.starts_at).format('D MMM YYYY HH:mm') : 'now'} → {r.ends_at ? dayjs(r.ends_at).format('D MMM YYYY HH:mm') : 'no end'}
                </div>
                <div style={{ color: '#888' }}>priority {r.priority}</div>
              </div>
            ),
          },
          {
            title: 'Who sees it',
            key: 'target',
            width: 220,
            render: (_, r) => {
              const bits = [];
              if (r.target_plan_ids) bits.push('plans: ' + r.target_plan_ids.split(',').map((id) => planName[id] || `#${id}`).join(', '));
              if (r.target_states) bits.push('states: ' + r.target_states);
              if (r.target_categories) bits.push(r.target_categories);
              if (r.target_policy_nos) bits.push(`${r.target_policy_nos.split(',').length} listed policies`);
              if (r.target_org_ids) bits.push('orgs: ' + r.target_org_ids.split(',').map((id) => orgName[id] || `#${id}`).join(', '));
              if (r.target_access_types) bits.push('access: ' + r.target_access_types.split(',').map((a) => accessLabel[a] || a).join(', '));
              return <span style={{ fontSize: 12 }}>{bits.length ? bits.join(' · ') : 'everyone'}</span>;
            },
          },
          {
            title: '',
            key: 'actions',
            width: 110,
            render: (_, r) => (
              <Space>
                <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
                {Number(r.status) === 1 ? (
                  <Popconfirm title="Archive this announcement?" description="It leaves the app immediately. Its id stays reserved." okText="Archive" okButtonProps={{ danger: true }} onConfirm={() => archive(r)}>
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                ) : null}
              </Space>
            ),
          },
        ]}
      />

      <Modal
        open={editing !== null}
        title={editing?.id ? 'Edit announcement' : 'New announcement'}
        onCancel={() => setEditing(null)}
        onOk={save}
        okText={editing?.id ? 'Save changes' : 'Publish'}
        confirmLoading={saving}
        width={720}
        destroyOnClose
      >
        <Card size="small" style={{ marginBottom: 12 }}>
          <Text type="secondary" style={{ fontSize: 11 }}>Preview (short text does not scroll; long text scrolls with the button pinned right)</Text>
          <div style={{ marginTop: 6 }}><BannerPreview message={message} ctaLabel={ctaKind === 'none' ? '' : ctaLabel} /></div>
        </Card>
        <Form form={form} layout="vertical">
          <Form.Item
            name="message"
            label="Message (plain text)"
            rules={[{ required: true, message: 'Write the message' }, { max: 500, message: '500 characters maximum' }]}
            extra={message && message.length > 160 ? <Text type="warning">{message.length} characters — over 160 it scrolls for a long time. Shorter is better.</Text> : `${(message || '').length} / 160 recommended`}
          >
            <Input.TextArea rows={2} maxLength={500} placeholder="We have added 24 new hospitals and clinics to your plan this month." />
          </Form.Item>

          <Form.Item name="cta_kind" label="Button" initialValue="none">
            <Radio.Group>
              <Radio.Button value="none">No button</Radio.Button>
              <Radio.Button value="route">Open a screen in the app</Radio.Button>
              <Radio.Button value="url">Open a web page</Radio.Button>
            </Radio.Group>
          </Form.Item>
          {ctaKind && ctaKind !== 'none' ? (
            <Space align="start" style={{ width: '100%' }} wrap>
              <Form.Item
                name="cta_label"
                label="Button label"
                rules={[{ required: true, message: 'Label is required with a button' }, { max: 40 }]}
                extra={ctaLabel && ctaLabel.length > 12 ? <Text type="warning">Over 12 characters is cut off with … in the app.</Text> : 'One or two words: "Find one", "Renew", "Book now"'}
              >
                <Input style={{ width: 200 }} placeholder="Find one" />
              </Form.Item>
              {ctaKind === 'route' ? (
                <Form.Item name="cta_route" label="Opens" rules={[{ required: true, message: 'Pick a section' }]} extra="A high-level app section. Deep links to searches, filters or facilities are not offered.">
                  <Select
                    style={{ width: 300 }}
                    showSearch
                    optionFilterProp="label"
                    options={(options.landing_pages && options.landing_pages.length
                      ? options.landing_pages.map((l) => ({ value: l.href, label: l.label }))
                      : (options.routes || []).map((r) => ({ value: r, label: r })))}
                  />
                </Form.Item>
              ) : (
                <Form.Item name="cta_url" label="Web address (https only)" rules={[{ required: true, message: 'Enter the https:// address' }, { pattern: /^https:\/\/.+/i, message: 'Must start with https://' }]}>
                  <Input style={{ width: 320 }} placeholder="https://amanhmo.com/renew" />
                </Form.Item>
              )}
            </Space>
          ) : null}

          <Space align="start" wrap>
            <Form.Item name="window" label="Live from → until (Lagos time)" extra="Leave the start empty for 'now' and the end empty for 'no end'.">
              <DatePicker.RangePicker showTime format="D MMM YYYY HH:mm" allowEmpty={[true, true]} />
            </Form.Item>
            <Form.Item name="priority" label="Priority" extra="Higher wins when several are live." initialValue={0}>
              <InputNumber min={0} max={1000} />
            </Form.Item>
          </Space>

          <Card size="small" title="Who sees it (leave everything empty for everyone)" style={{ marginBottom: 12 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Form.Item name="target_org_ids" label="Organisations" style={{ marginBottom: 8 }}>
                <Select mode="multiple" allowClear placeholder="All organisations" options={(options.organizations || []).map((o) => ({ value: o.org_id, label: o.org_name }))} optionFilterProp="label" showSearch />
              </Form.Item>
              <Form.Item name="target_access_types" label="Access type" style={{ marginBottom: 8 }} extra="Principal / employee, spouse, child … Combine with organisation and plan: only members matching every filter receive it.">
                <Select mode="multiple" allowClear placeholder="All access types" options={(options.access_types || []).map((a) => ({ value: a.value, label: a.label }))} />
              </Form.Item>
              <Form.Item name="target_plan_ids" label="Plans" style={{ marginBottom: 8 }}>
                <Select mode="multiple" allowClear placeholder="All plans" options={(options.plans || []).map((p) => ({ value: p.plan_id, label: p.plan_name }))} optionFilterProp="label" />
              </Form.Item>
              <Form.Item name="target_states" label="States" style={{ marginBottom: 8 }}>
                <Select mode="multiple" allowClear placeholder="All states" options={states.map((s) => ({ value: s.value, label: s.label }))} optionFilterProp="label" />
              </Form.Item>
              <Form.Item name="target_categories" label="Member type" style={{ marginBottom: 8 }}>
                <Select mode="multiple" allowClear placeholder="Self pay and corporate" options={(options.categories || []).map((c) => ({ value: c, label: c }))} />
              </Form.Item>
              <Form.Item name="target_policy_nos" label="Specific policy numbers" style={{ marginBottom: 0 }} extra="One per line or comma-separated. For 'your premium is due' style messages.">
                <Input.TextArea rows={2} />
              </Form.Item>
            </Space>
          </Card>

          {editing?.id ? (
            <Form.Item name="public_id" label="Id (permanent)">
              <Input disabled />
            </Form.Item>
          ) : (
            <Form.Item name="public_id" label="Id (optional)" extra="Leave empty to generate one from the message and month. Ids are permanent and never reused: phones remember dismissals against them.">
              <Input placeholder="new-facilities-2026-09" />
            </Form.Item>
          )}
        </Form>
        <Alert type="info" showIcon message="Members see one announcement at a time. If several are live, the highest priority shows; the others surface as it expires or is dismissed." />
      </Modal>
    </div>
  );
}
